// ============================================================
// ADVANCE MONTHLY RECURRENCE
// ============================================================
//
// Returns the NEXT actual scheduled monthly occurrence after
// currentDate.
//
// Supports:
//
// 1. Specific day of month
//      Example: 15th of every month
//
// 2. Calendar-anchored day of month
//      Example: 31st
//
//      Jan 31
//      Feb 28
//      Mar 31
//      Apr 30
//      May 31
//
// 3. Nth weekday
//      Example: 2nd Tuesday
//
// 4. Multiple nth weekdays
//      Example: 2nd and 4th Wednesday
//
// 5. Last weekday
//      Example: last Wednesday
//
// interval_value represents the number of MONTHS between
// recurrence months.
//
// ============================================================


// ============================================================
// LOCAL CALENDAR DATE
// ============================================================

function toLocalCalendarDate(value) {

    if (!value) {
        return null
    }


    if (value instanceof Date) {

        return new Date(
            value.getFullYear(),
            value.getMonth(),
            value.getDate()
        )
    }


    if (
        typeof value === 'string' &&
        /^\d{4}-\d{2}-\d{2}$/.test(value)
    ) {

        const [
            year,
            month,
            day
        ] =
            value
                .split('-')
                .map(Number)

        return new Date(
            year,
            month - 1,
            day
        )
    }


    const parsed =
        new Date(value)

    if (
        Number.isNaN(
            parsed.getTime()
        )
    ) {
        return null
    }


    return new Date(
        parsed.getFullYear(),
        parsed.getMonth(),
        parsed.getDate()
    )
}


// ============================================================
// DAYS IN MONTH
// ============================================================

function getDaysInMonth(
    year,
    month
) {

    return new Date(
        year,
        month + 1,
        0
    ).getDate()
}


// ============================================================
// ADD MONTHS
// ============================================================
//
// Adds whole calendar months without allowing JavaScript's
// Date object to overflow into the following month.
//
// Example:
//
// Jan 31 + 1 month
//
// becomes the last valid day of February:
//
// Feb 28
//
// This helper is primarily used for moving between recurrence
// months. The actual day-of-month occurrence is calculated
// separately so that the original anchor is never lost.
//
// ============================================================

function addMonths(
    date,
    months
) {

    const result =
        new Date(
            date.getFullYear(),
            date.getMonth(),
            1
        )

    result.setMonth(
        result.getMonth() + months
    )

    return result
}


// ============================================================
// MONTH OFFSET
// ============================================================
//
// Returns the number of calendar months between two dates.
//
// Example:
//
// January 2026 → January 2026 = 0
// January 2026 → February 2026 = 1
// January 2026 → March 2026 = 2
//
// ============================================================

function monthsBetween(
    startDate,
    currentDate
) {

    return (
        (
            currentDate.getFullYear() -
            startDate.getFullYear()
        ) * 12
        +
        (
            currentDate.getMonth() -
            startDate.getMonth()
        )
    )
}


// ============================================================
// NTH WEEKDAY OF MONTH
// ============================================================

function getNthWeekdayDate(
    year,
    month,
    weekday,
    occurrence
) {

    const firstOfMonth =
        new Date(
            year,
            month,
            1
        )


    const firstWeekday =
        firstOfMonth.getDay()


    const offset =
        (
            weekday -
            firstWeekday +
            7
        ) % 7


    const day =
        1 +
        offset +
        (
            (occurrence - 1) *
            7
        )


    const daysInMonth =
        getDaysInMonth(
            year,
            month
        )


    if (
        day >
        daysInMonth
    ) {
        return null
    }


    return new Date(
        year,
        month,
        day
    )
}


// ============================================================
// LAST WEEKDAY OF MONTH
// ============================================================

function getLastWeekdayDate(
    year,
    month,
    weekday
) {

    const lastDay =
        new Date(
            year,
            month + 1,
            0
        )


    const lastWeekday =
        lastDay.getDay()


    const offset =
        (
            lastWeekday -
            weekday +
            7
        ) % 7


    return new Date(
        year,
        month + 1,
        0 - offset
    )
}


// ============================================================
// NORMALIZE WEEK NUMBERS
// ============================================================

function getWeekNumbers(
    value
) {

    let numbers =
        value


    if (
        typeof numbers === 'string'
    ) {

        try {

            numbers =
                JSON.parse(
                    numbers
                )

        } catch {

            numbers =
                numbers
                    .split(',')
                    .map(
                        value =>
                            Number(
                                value.trim()
                            )
                    )
        }
    }


    if (
        !Array.isArray(
            numbers
        )
    ) {

        return []
    }


    return [
        ...new Set(
            numbers
                .map(Number)
                .filter(
                    number =>
                        Number.isInteger(
                            number
                        ) &&
                        (
                            (
                                number >= 1 &&
                                number <= 5
                            ) ||
                            number === -1
                        )
                )
        )
    ].sort(
        (a, b) =>
            a - b
    )
}


// ============================================================
// GET MONTHLY CANDIDATES
// ============================================================
//
// Returns all valid scheduled dates for a particular month.
//
// IMPORTANT:
//
// For day_of_month rules, the configured day is treated as a
// calendar anchor.
//
// Example:
//
// configured day = 31
//
// February → 28
// March    → 31
// April    → 30
//
// ============================================================

function getMonthlyCandidates(
    template,
    year,
    month
) {

    const candidates = []


    const ruleType =
        template.monthly_rule_type


    // ========================================================
    // DAY OF MONTH
    // ========================================================

    if (
        ruleType === 'day_of_month'
    ) {

        const dayOfMonth =
            Number(
                template.day_of_month
            )


        if (
            !Number.isInteger(
                dayOfMonth
            ) ||
            dayOfMonth < 1 ||
            dayOfMonth > 31
        ) {

            return candidates
        }


        const daysInMonth =
            getDaysInMonth(
                year,
                month
            )


        // ----------------------------------------------------
        // Calendar anchor.
        //
        // 31 becomes 28 in February,
        // 30 in April, etc.
        // ----------------------------------------------------

        const actualDay =
            Math.min(
                dayOfMonth,
                daysInMonth
            )


        candidates.push(
            new Date(
                year,
                month,
                actualDay
            )
        )


        return candidates
    }


    // ========================================================
    // NTH WEEKDAY
    // ========================================================

    if (
        ruleType === 'nth_weekday'
    ) {

        const weekday =
            Number(
                template.monthly_weekday
            )


        if (
            !Number.isInteger(
                weekday
            ) ||
            weekday < 0 ||
            weekday > 6
        ) {

            return candidates
        }


        const weekNumbers =
            getWeekNumbers(
                template.monthly_week_numbers
            )


        for (
            const occurrence
            of weekNumbers
        ) {

            // ------------------------------------------------
            // Last weekday.
            // ------------------------------------------------

            if (
                occurrence === -1
            ) {

                candidates.push(
                    getLastWeekdayDate(
                        year,
                        month,
                        weekday
                    )
                )

                continue
            }


            // ------------------------------------------------
            // Normal nth weekday.
            // ------------------------------------------------

            const date =
                getNthWeekdayDate(
                    year,
                    month,
                    weekday,
                    occurrence
                )


            if (date) {

                candidates.push(
                    date
                )
            }
        }
    }


    // ========================================================
    // SORT AND DEDUPLICATE
    // ========================================================

    return [
        ...new Map(
            candidates.map(
                date => [
                    date
                        .toISOString()
                        .slice(0, 10),
                    date
                ]
            )
        ).values()
    ].sort(
        (a, b) =>
            a.getTime() -
            b.getTime()
    )
}


// ============================================================
// ADVANCE MONTHLY
// ============================================================

export function advanceMonthly({

    template,

    currentDate

}) {

    const current =
        toLocalCalendarDate(
            currentDate
        )


    if (!current) {

        return null
    }


    // ========================================================
    // RECURRENCE START
    // ========================================================

    const startDate =
        toLocalCalendarDate(
            template.recurrence_start_date
        )


    if (!startDate) {

        return null
    }


    // ========================================================
    // MONTHLY INTERVAL
    // ========================================================
    //
    // NULL = every month.
    //
    // 1 = every month
    // 2 = every other month
    // 3 = every third month
    //
    // ========================================================

    const interval =
        Math.max(
            1,
            Number(
                template.interval_value
            ) || 1
        )


    // ========================================================
    // SEARCH CURRENT MONTH
    // ========================================================
    //
    // This matters for rules such as:
    //
    // 2nd and 4th Wednesday
    //
    // If currentDate is the 2nd Wednesday, the next occurrence
    // is the 4th Wednesday in the SAME month.
    //
    // The current month must also be a valid recurrence month.
    //
    // ========================================================

    const currentMonthOffset =
        monthsBetween(
            startDate,
            current
        )


    if (
        currentMonthOffset >= 0 &&
        currentMonthOffset % interval === 0
    ) {

        const candidates =
            getMonthlyCandidates(
                template,
                current.getFullYear(),
                current.getMonth()
            )


        const nextSameMonth =
            candidates.find(
                date =>
                    date > current
            )


        if (
            nextSameMonth
        ) {

            return nextSameMonth
        }
    }


    // ========================================================
    // SEARCH FUTURE RECURRENCE MONTHS
    // ========================================================
    //
    // Start from the NEXT valid recurrence month based on the
    // original recurrence start date.
    //
    // This is what prevents interval drift.
    //
    // Example:
    //
    // start = January
    // interval = 2
    //
    // January
    // March
    // May
    // July
    //
    // ========================================================

    let nextMonthOffset =
        Math.max(
            0,
            currentMonthOffset + 1
        )


    const remainder =
        nextMonthOffset %
        interval


    if (
        remainder !== 0
    ) {

        nextMonthOffset +=
            interval -
            remainder
    }


    // --------------------------------------------------------
    // Search up to ten years of monthly calendar time.
    // --------------------------------------------------------

    const MAX_MONTHS =
        120


    for (
        let i = 0;
        i < MAX_MONTHS;
        i++
    ) {

        const monthDate =
            addMonths(
                startDate,
                nextMonthOffset
            )


        const candidates =
            getMonthlyCandidates(
                template,
                monthDate.getFullYear(),
                monthDate.getMonth()
            )


        if (
            candidates.length > 0
        ) {

            // ------------------------------------------------
            // Return the first valid scheduled date in the
            // recurrence month.
            // ------------------------------------------------

            return candidates[0]
        }


        // ----------------------------------------------------
        // No valid candidate in this month.
        //
        // Move to the next recurrence month.
        // ----------------------------------------------------

        nextMonthOffset +=
            interval
    }


    return null
}